import { DOCUMENT, isPlatformServer } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, REQUEST } from '@angular/core';

export interface CookieOptions {
  maxAgeSeconds?: number;
  path?: string;
  sameSite?: 'Lax' | 'Strict' | 'None';
  secure?: boolean;
}

const DEFAULT_COOKIE_PATH: string = '/';
const DEFAULT_COOKIE_SAME_SITE: 'Lax' | 'Strict' | 'None' = 'Lax';

@Injectable({ providedIn: 'root' })
export class CookieService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly isServer: boolean = isPlatformServer(inject(PLATFORM_ID));
  private readonly request: Request | null = inject(REQUEST, { optional: true });

  public get(name: string): string | undefined {
    const cookieHeader: string | null | undefined = this.isServer
      ? this.request?.headers.get('cookie')
      : this.document.cookie;

    return this.getCookieValue(cookieHeader, name);
  }

  public set(name: string, value: string, options: CookieOptions = {}): void {
    if (this.isServer) {
      return;
    }

    const cookieParts: string[] = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      `Path=${options.path ?? DEFAULT_COOKIE_PATH}`,
      `SameSite=${options.sameSite ?? DEFAULT_COOKIE_SAME_SITE}`,
    ];

    if (options.maxAgeSeconds !== undefined) {
      cookieParts.splice(1, 0, `Max-Age=${options.maxAgeSeconds}`);
    }

    if (this.shouldUseSecureCookie(options)) {
      cookieParts.push('Secure');
    }

    this.document.cookie = cookieParts.join('; ');
  }

  private getCookieValue(cookieHeader: string | null | undefined, name: string): string | undefined {
    if (!cookieHeader) {
      return undefined;
    }

    const cookieName: string = `${encodeURIComponent(name)}=`;
    const cookie: string | undefined = cookieHeader
      .split(';')
      .map((value: string): string => value.trim())
      .find((value: string): boolean => value.startsWith(cookieName));

    if (!cookie) {
      return undefined;
    }

    return this.decodeCookieValue(cookie.slice(cookieName.length));
  }

  private shouldUseSecureCookie(options: CookieOptions): boolean {
    const isHttps: boolean = this.document.defaultView?.location.protocol === 'https:';

    return options.secure ?? isHttps;
  }

  private decodeCookieValue(value: string): string | undefined {
    try {
      return decodeURIComponent(value);
    } catch (error: unknown) {
      console.error('Could not decode cookie value.', error);

      return undefined;
    }
  }
}
